import React, { useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import Check from '@mui/icons-material/Check';
import { ArcIconButton, ArcText, ArcTooltip } from '@src/core/ui';
import { ModeIcon } from './icons';
import { CAMERA_TOOLBAR_ICON_BUTTON_SX } from './toolbar-icon-button-sx';
import { useCameraLayout } from './camera-layout-context';
import type { CameraViewMode } from './types';
import {
  cameraMenuDropdownCaptionSx,
  cameraMenuDropdownMenuItemSx,
  cameraMenuDropdownPaperSx,
} from './camera-menu-dropdown-sx';

const MODES: { mode: CameraViewMode; label: string }[] = [
  { mode: 'default', label: 'Default' },
  { mode: 'pip', label: 'Swap' },
  { mode: 'split', label: 'Split' },
];

const VideoModeSelector: React.FC = () => {
  const { viewMode, setViewMode } = useCameraLayout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSelect = (mode: CameraViewMode) => {
    setViewMode(mode);
    setAnchorEl(null);
  };

  return (
    <>
      <ArcTooltip title="View Mode">
        <ArcIconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={CAMERA_TOOLBAR_ICON_BUTTON_SX}>
          <ModeIcon />
        </ArcIconButton>
      </ArcTooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: cameraMenuDropdownPaperSx,
          },
        }}>
        <Box
          role="presentation"
          sx={{
            px: 1.25,
            pb: 0.5,
            textAlign: 'center',
            pointerEvents: 'none',
          }}>
          <ArcText.caption sx={cameraMenuDropdownCaptionSx}>View Mode</ArcText.caption>
        </Box>
        {MODES.map(({ mode, label }) => (
          <MenuItem
            key={mode}
            selected={viewMode === mode}
            onClick={() => handleSelect(mode)}
            sx={cameraMenuDropdownMenuItemSx}>
            <Box sx={{ width: 10, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              {viewMode === mode ? <Check sx={{ fontSize: 14, color: 'common.white' }} /> : null}
            </Box>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default VideoModeSelector;
